import {createRef, useContext, useEffect, useState} from 'react';
import Modal from 'react-modal';

import Input from '../../../base_components/Input';
import Select from '../../../base_components/select/Select';
import Context from '../Context';
import _axios from '../../../utils/axios';
import validate from '../../../utils/validator';
import {ADMIN, DEFAULT_USER_TYPE} from '../../../utils/settings';
import {handleError} from '../../../utils/utils';

import styles from '../../../styles/users/CreatingUser.module.scss';
import buttonStyles from '../../../styles/base_components/Button.module.scss';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(40, 40, 64, 0.5)'
  },
  content: {
    width: '80vw',
    minHeight: '35vh',
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    border: 'none',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#303253'
  },
};
Modal.setAppElement('#root');

/**
 * @param {string[]} props.types
 */
function CreatingUser(props) {
  const context = useContext(Context);

  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [roles, setRoles] = useState({});
  const [placeholder, setPlaceholder] = useState('');
  const [rolesError, setRolesError] = useState('');

  const [modal, setModal] = useState(false);

  const passwordRef = createRef();
  const [passwordError, setPasswordError] = useState('');

  const confirmPasswordRef = createRef();
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  function clearRoles() {
    let r = {};
    for (let group in context.groups) {
      for (let type of props.types) {
        r[group + ' ' + type] = (group === context.defaultGroup && type === DEFAULT_USER_TYPE);
      }
    }
    setRoles(r);
    setPlaceholder(context.defaultGroup + ' ' + DEFAULT_USER_TYPE);
  }

  function onSetPassword(event) {
    if (!username || !email) return;
    if (usernameError || emailError || rolesError) return;
    const [usernameErr, emailErr, rolesErr] = validate([
      {value: username, type: 'username'},
      {value: email, type: 'email'},
      {value: roles, type: 'select'},
    ]);
    if (usernameErr) setUsernameError(usernameErr);
    if (emailErr) setEmailError(emailErr);
    if (rolesErr) setRolesError(rolesErr);
    if (usernameErr || emailErr || rolesErr) return;

    setModal(true);
  }

  function validateUser(response) {
    const {ok, data} = response.data;
    if (typeof ok !== 'number' || typeof data !== 'object') throw new Error('The database does not correspond to our standard.');
    if (ok !== 1) throw new Error('The database is currently unavailable.');

    let err = validate([
      {value: data.id, type: 'db_user_id'},
      {value: data.username, type: 'db_user_username'},
      {value: data.email, type: 'db_user_email'},
      {value: data.groups, type: 'db_user_groups'},
      {value: data.roles, type: 'db_user_roles'},
      {value: data.enabled, type: 'db_user_enabled', empty: true}
    ]);
    err = err.filter(e => e !== '');
    if (err.length !== 0) throw new Error('Error at validating ' + data.host + ': ' + err.join('. '));
  }

  function onSubmit(event) {
    if (!passwordRef.current || !confirmPasswordRef.current) return;
    if (passwordError || confirmPasswordError) return;
    const [passwordErr, confirmPasswordErr] = validate([
      {value: passwordRef.current.value, type: 'password'},
      {
        value: {
          password: passwordRef.current.value,
          confirm: confirmPasswordRef.current.value
        },
        type: 'confirmPassword'
      },
    ]);
    if (passwordErr) setPasswordError(passwordErr);
    if (confirmPasswordErr) setConfirmPasswordError(confirmPasswordErr);
    if (passwordErr || confirmPasswordErr) return;

    let groups = [];
    let types = [];
    for (let role in roles) {
      if (!roles[role]) continue;

      const [group, type] = role.split(' ');
      groups.push(group);
      types.push(type === 'admin' ? group + '_admin' : group + '_user');
    }

    const data = {
      username: username,
      email: email,
      password: passwordRef.current.value,
      groups: groups,
      roles: types,
      enabled: true
    };

    _axios.post(ADMIN.USERS_PATH, data)
      .then(response => {
        validateUser(response);

        setModal(false);
        setUsername('');
        setEmail('');
        clearRoles();

        context.setUsers(users => {
          let u = {};
          u[response.data.data.id] = {
            username: response.data.data.username,
            email: response.data.data.email,
            groups: response.data.data.groups,
            roles: response.data.data.roles,
            enabled: response.data.data.enabled
          };
          return {...u, ...users};
        });
      })
      .catch(handleError);
  }

  function onCancel(event) {
    setPasswordError('');
    setConfirmPasswordError('');
    setModal(false);
  }

  function onCloseModal(event) {
    setModal(false);
  }

  useEffect(() => {
    function clearRoles() {
      let r = {};
      for (let group in context.groups) {
        for (let type of props.types) {
          r[group + ' ' + type] = (group === context.defaultGroup && type === DEFAULT_USER_TYPE);
        }
      }
      setRoles(r);
      setPlaceholder(context.defaultGroup + ' ' + DEFAULT_USER_TYPE);
    }

    clearRoles();
  }, [context.groups, props.types, context.defaultGroup]);

  return (
    <div className={styles.creatingUser + ' ' + props.className}>
      <h2>Create user</h2>
      <div className={styles.inputWrapper}>
        <Input
          className={styles.input}
          placeholder='Username'
          value={username}
          setValue={setUsername}
          error={usernameError}
          setError={setUsernameError}
        />
        <Input
          className={styles.input}
          placeholder='Email'
          value={email}
          setValue={setEmail}
          error={emailError}
          setError={setEmailError}
        />
        <Select
          className={styles.select}
          placeholder={placeholder ? placeholder : 'Enter group name'}
          setPlaceholder={setPlaceholder}
          items={roles}
          setItems={setRoles}
          error={rolesError}
          setError={setRolesError}
        />
      </div>
      <button className={buttonStyles.button} onClick={onSetPassword}>set password</button>

      <Modal
        isOpen={modal}
        onRequestClose={onCloseModal}
        style={customStyles}
      >
        <div className={styles.modal}>
          <h2>Set password</h2>
          <Input
            className={styles.input}
            placeholder='Password'
            type='password'
            ref={passwordRef}
            error={passwordError}
            setError={setPasswordError}
          />
          <Input
            className={styles.input}
            placeholder='Confirm password'
            type='password'
            ref={confirmPasswordRef}
            error={confirmPasswordError}
            setError={setConfirmPasswordError}
          />

          <div className={styles.buttons}>
            <button className={buttonStyles.button} onClick={onSubmit}>submit</button>
            <button className={buttonStyles.button} onClick={onCancel}>cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CreatingUser;